import { useEffect, useRef, useState } from 'react'
import {Download, FileText, Loader2} from 'lucide-react'
import { jsPDF } from 'jspdf'
import toast from 'react-hot-toast'
import { carregarImagem } from '../utils/carregarTemplate'

interface PanfletoPequenoProps {
  usuarioConsultora: string
}

const PANFLETO_A5_TEMPLATE_URL =
  'https://static.lumi.new/55/55df22f8c059e918e69cb61a543e01db.webp'

async function montarPanfletoCanvas(
  template: HTMLImageElement,
): Promise<HTMLCanvasElement> {
  const canvas = document.createElement('canvas')
  canvas.width = template.naturalWidth
  canvas.height = template.naturalHeight
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas não disponível.')

  ctx.drawImage(template, 0, 0, canvas.width, canvas.height)

  return canvas
}

export default function PanfletoPequeno({
  usuarioConsultora,
}: PanfletoPequenoProps) {
  const previewRef = useRef<HTMLCanvasElement | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erroPreview, setErroPreview] = useState(false)
  const [baixandoPdf, setBaixandoPdf] = useState(false)
  const [baixandoPng, setBaixandoPng] = useState(false)

  useEffect(() => {
    let ativo = true
    setCarregando(true)
    setErroPreview(false)
    ;(async () => {
      try {
        const template = await carregarImagem(PANFLETO_A5_TEMPLATE_URL)
        const canvas = await montarPanfletoCanvas(template)
        if (!ativo) return
        const ctx = previewRef.current?.getContext('2d')
        if (previewRef.current && ctx) {
          previewRef.current.width = canvas.width
          previewRef.current.height = canvas.height
          ctx.drawImage(canvas, 0, 0)
        }
      } catch (e) {
        console.error('Erro ao gerar pré-visualização do panfleto pequeno:', e)
        if (ativo) setErroPreview(true)
      } finally {
        if (ativo) setCarregando(false)
      }
    })()
    return () => {
      ativo = false
    }
  }, [usuarioConsultora])

  async function gerarCanvasFinal(): Promise<HTMLCanvasElement> {
    const template = await carregarImagem(PANFLETO_A5_TEMPLATE_URL)
    return await montarPanfletoCanvas(template)
  }

  async function baixarPng() {
    if (baixandoPng) return
    setBaixandoPng(true)
    try {
      const canvas = await gerarCanvasFinal()
      try {
        canvas.toBlob(
          blob => {
            if (!blob) {
              toast.error('Erro ao gerar PNG.')
              return
            }
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `panfleto-pequeno-${usuarioConsultora}.png`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            setTimeout(() => URL.revokeObjectURL(url), 10000)
            toast.success('Panfleto PNG baixado!')
          },
          'image/png',
          1.0,
        )
      } catch (corsErr) {
        console.error('Canvas bloqueado por restrição de segurança da imagem:', corsErr)
        toast.error('Não foi possível gerar o PNG agora. Atualize a página e tente novamente.')
      }
    } catch (e) {
      console.error(e)
      toast.error('Erro ao gerar o panfleto em PNG.')
    } finally {
      setBaixandoPng(false)
    }
  }

  async function baixarPdf() {
    if (baixandoPdf) return
    setBaixandoPdf(true)
    try {
      const canvas = await gerarCanvasFinal()
      let dataUrl: string
      try {
        dataUrl = canvas.toDataURL('image/jpeg', 0.95)
      } catch (corsErr) {
        console.error('Canvas bloqueado por restrição de segurança da imagem:', corsErr)
        toast.error('Não foi possível gerar o PDF agora. Atualize a página e tente novamente.')
        return
      }

      // O template é retrato (largura < altura), formato A5 em mm: 148 x 210
      const ratio = canvas.width / canvas.height
      let pdfW = 148
      let pdfH = 210
      // Se o template for mais largo do que alto (paisagem), inverter dimensões
      if (ratio > 1) {
        pdfW = 210
        pdfH = 148
      }

      const pdf = new jsPDF({
        orientation: ratio > 1 ? 'landscape' : 'portrait',
        unit: 'mm',
        format: 'a5',
        compress: true,
      })

      pdf.addImage(dataUrl, 'JPEG', 0, 0, pdfW, pdfH, undefined, 'FAST')
      pdf.save(`panfleto-pequeno-${usuarioConsultora}.pdf`)
      toast.success('Panfleto PDF baixado!')
    } catch (e) {
      console.error(e)
      toast.error('Erro ao gerar o panfleto em PDF.')
    } finally {
      setBaixandoPdf(false)
    }
  }

  return (
    <div>
      <p className="text-sm text-texto-escuro leading-relaxed mb-3">
        Baixe seu panfleto A5 e inclua o QR Code. Após isso estará pronto para
        distribuir em consultas, atendimentos e eventos de bem-estar.
      </p>

      {/* Instrução de como incluir QR Code */}
      <div className="mb-5 bg-[#F8F4EF] border border-[#EDE0D4] rounded-xl px-4 py-3 text-xs leading-relaxed" style={{ color: '#666666' }}>
        <p className="font-semibold mb-1">💡 Como incluir seu QR Code:</p>
        <p className="mb-1">Você pode adicionar seu QR Code personalizado de duas formas:</p>
        <p className="mb-0.5">→ Solicite na gráfica no momento da impressão informando a posição correta</p>
        <p className="mb-2">→ Utilize uma IA de edição de imagem para inserir seu QR Code no panfleto antes de imprimir</p>
        <p>Seu QR Code personalizado está disponível na seção <strong style={{ color: '#555' }}>'Seus QR Codes Personalizados'</strong> acima.</p>
      </div>

      {/* Pré-visualização */}
      <div className="mb-5 flex justify-center">
        <div
          className="relative bg-bege-fundo border-2 border-bege-medio rounded-2xl overflow-hidden shadow-md"
          style={{ width: 280, aspectRatio: `${1 / 1}` }}
        >
          {carregando && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-verde-escuro">
              <Loader2 className="w-6 h-6 animate-spin" />
              <span className="text-xs">Gerando pré-visualização...</span>
            </div>
          )}
          {erroPreview && !carregando && (
            <div className="absolute inset-0 flex items-center justify-center text-xs text-red-600 px-4 text-center">
              Não foi possível carregar a pré-visualização. Tente baixar
              diretamente nos botões abaixo.
            </div>
          )}
          <canvas
            ref={previewRef}
            className="w-full h-full block"
            style={{ display: carregando || erroPreview ? 'none' : 'block' }}
          />
        </div>
      </div>

      {/* Botões de download */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          onClick={baixarPdf}
          disabled={baixandoPdf || carregando}
          className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-medium text-sm bg-verde-escuro text-white hover:bg-opacity-90 transition-all min-h-[48px] disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {baixandoPdf ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <FileText className="w-4 h-4" />
          )}
          {baixandoPdf ? 'Gerando PDF...' : 'Baixar meu panfleto pequeno em PDF'}
        </button>

        <button
          onClick={baixarPng}
          disabled={baixandoPng || carregando}
          className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-medium text-sm bg-white text-verde-escuro border-2 border-verde-escuro hover:bg-verde-muito-claro transition-all min-h-[48px] disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {baixandoPng ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Download className="w-4 h-4" />
          )}
          {baixandoPng ? 'Gerando PNG...' : 'Baixar meu panfleto pequeno em PNG'}
        </button>
      </div>

      <div className="mt-4 bg-[#FFF7ED] border border-[#FED7AA] rounded-xl px-4 py-3 text-sm text-[#92400E] leading-relaxed">
        💡 O <strong>PDF</strong> é ideal para impressão em alta qualidade
        (formato A5 ou A6). O <strong>PNG</strong> é ideal para compartilhar
        digitalmente em redes sociais e WhatsApp.
      </div>
    </div>
  )
}
